import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Clock, ChevronRight, Undo2, CheckCircle2, Wine, Utensils, Beer, Package, Sparkles, Filter, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import "./pos.css";

const POLL_MS = 4000;

const COLUMNS = [
  { key: "pending",   label: "En attente",   next: "preparing", icon: Clock,        accent: "text-amber-400" },
  { key: "preparing", label: "En préparation", next: "ready",     icon: Wine,         accent: "text-blue-400" },
  { key: "ready",     label: "Prêt",         next: "served",    icon: CheckCircle2, accent: "text-emerald-400" },
  { key: "served",    label: "Servi",        next: null,        icon: CheckCircle2, accent: "text-muted-foreground" },
];

const KIND_META = {
  cocktail: { label: "Cocktail", icon: Wine, color: "border-primary/40 bg-primary/5" },
  beer:     { label: "Bière",    icon: Beer, color: "border-amber-500/40 bg-amber-500/5" },
  snack:    { label: "Snack",    icon: Utensils, color: "border-emerald-500/40 bg-emerald-500/5" },
  bottle:   { label: "Bouteille", icon: Package, color: "border-neutral-500/40 bg-neutral-500/5" },
  custom:   { label: "Libre",    icon: Sparkles, color: "border-purple-500/40 bg-purple-500/5" },
};

const FILTERS = [
  { key: "all",       label: "Tout" },
  { key: "cocktail",  label: "Cocktails" },
  { key: "beer",      label: "Bières" },
  { key: "snack",     label: "Snacks" },
  { key: "bottle",    label: "Bouteilles" },
];

function elapsed(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h${String(m % 60).padStart(2, "0")}`;
}

function ageBadgeClass(iso, status) {
  if (status !== "pending" && status !== "preparing") return "text-muted-foreground";
  if (!iso) return "text-muted-foreground";
  const secs = (Date.now() - new Date(iso).getTime()) / 1000;
  if (secs > 600) return "text-red-400 font-semibold";      // >10 min: rouge
  if (secs > 300) return "text-amber-400";                  // >5 min: ambre
  return "text-muted-foreground";
}

/**
 * Alert severity for the whole ticket card.
 * - critical (red): >5 min waiting for the bar to start OR >10 min preparing
 *   → red border + pulse + warning icon
 * - warn (amber): >2 min pending — subtle amber ring
 * - null: fresh ticket, no styling change
 * Only pending/preparing tickets escalate; ready/served are neutral.
 */
export function alertLevel(iso, status) {
  if (status !== "pending" && status !== "preparing") return null;
  if (!iso) return null;
  const secs = (Date.now() - new Date(iso).getTime()) / 1000;
  const criticalAt = status === "pending" ? 300 : 600; // 5 min pending, 10 min preparing
  if (secs > criticalAt) return "critical";
  if (secs > 120) return "warn";
  return null;
}

export default function POSBar() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState({}); // line_id -> true during patch
  const timerRef = useRef(null);
  const [tick, setTick] = useState(0); // force re-render every 15s to refresh elapsed times

  const load = async () => {
    try {
      const { data } = await api.get("/pos/prep-queue");
      setItems(data.items || []);
      setError(null);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Erreur chargement";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, POLL_MS);
    // separate ticker so "elapsed" labels re-render even between poll cycles.
    // 5s cadence so a ticket entering the "> 5 min" red-alert zone lights up
    // reactively without waiting for the next fetch.
    const ticker = setInterval(() => setTick((t) => t + 1), 5000);
    return () => {
      clearInterval(timerRef.current);
      clearInterval(ticker);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const advance = async (item, target) => {
    const line = item.line;
    if (busy[line.id]) return;
    setBusy((b) => ({ ...b, [line.id]: true }));
    // Optimistic update
    setItems((prev) =>
      prev.map((it) =>
        it.line.id === line.id ? { ...it, line: { ...it.line, prep_status: target } } : it,
      ),
    );
    try {
      await api.patch(`/pos/orders/${item.order_id}/lines/${line.id}/prep-status`, {
        prep_status: target,
      });
      // refresh silently on success (keeps timestamps + FIFO order consistent)
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
      load();
    } finally {
      setBusy((b) => {
        const copy = { ...b };
        delete copy[line.id];
        return copy;
      });
    }
  };

  // -------- Filtered + grouped --------
  // `tick` is intentionally in the deps so the `filtered` array reference
  // changes every 5s — which invalidates the `counts` memo below, which is
  // what re-computes `critical` from wall-clock deltas.
  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((it) => it.line.kind === filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, filter, tick]);

  const columns = useMemo(() => {
    const map = { pending: [], preparing: [], ready: [], served: [] };
    for (const it of filtered) {
      const s = it.line.prep_status || "pending";
      if (map[s]) map[s].push(it);
    }
    return map;
  }, [filtered]);

  const counts = useMemo(() => {
    // Count critical alerts across pending + preparing so the header can
    // surface a global "n tickets en retard" badge at a glance.
    // `filtered` is included by reference (not `.length`) so a tick-driven
    // re-filter invalidates this memo and the badge refreshes as seconds
    // pass — even when no new item arrives from the backend.
    const criticalCount = filtered.reduce((acc, it) => {
      const ts = it.line.pending_at || it.line.preparing_at || it.line.added_at;
      return acc + (alertLevel(ts, it.line.prep_status) === "critical" ? 1 : 0);
    }, 0);
    return {
      total: filtered.length,
      pending: columns.pending.length,
      preparing: columns.preparing.length,
      ready: columns.ready.length,
      served: columns.served.length,
      critical: criticalCount,
    };
  }, [columns, filtered]);

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4" data-testid="pos-bar-page">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl flex items-center gap-3 flex-wrap">
            Bar & Cuisine — Tickets en direct
            {counts.critical > 0 && (
              <span
                className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full bg-red-500/20 border border-red-500/60 text-red-300 animate-pulse"
                data-testid="pos-bar-critical-badge"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {counts.critical} en retard
              </span>
            )}
          </h1>
          <p className="text-xs text-muted-foreground uppercase tracking-[0.2em] mt-1">
            {counts.total} item{counts.total !== 1 ? "s" : ""} · {counts.pending} en attente · {counts.preparing} en prépa · {counts.ready} prêt{counts.ready > 1 ? "s" : ""}
            · Rafraîchi toutes les {POLL_MS / 1000}s
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-muted-foreground" />
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`rounded-full h-9 px-4 text-sm border ${filter === f.key ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
              data-testid={`pos-bar-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      )}
      {error && !loading && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 text-destructive p-4 text-sm" data-testid="pos-bar-error">
          {error}
        </div>
      )}

      {/* Kanban 4 columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="pos-bar-kanban">
        {COLUMNS.map((col) => {
          const IconCol = col.icon;
          const list = columns[col.key] || [];
          return (
            <section
              key={col.key}
              className="rounded-2xl border border-border bg-card/60 p-3 flex flex-col min-h-[240px]"
              data-testid={`pos-bar-col-${col.key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <IconCol className={`w-4 h-4 ${col.accent}`} />
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{col.label}</div>
                    <div className={`font-display text-2xl ${col.accent}`}>{list.length}</div>
                  </div>
                </div>
              </div>

              <div className="flex-1 space-y-2 overflow-auto">
                {list.length === 0 ? (
                  <div className="text-center text-xs text-muted-foreground py-6" data-testid={`pos-bar-col-${col.key}-empty`}>
                    —
                  </div>
                ) : list.map((it) => (
                  <TicketCard
                    key={it.line.id}
                    item={it}
                    col={col}
                    onAdvance={() => col.next && advance(it, col.next)}
                    onRevert={() => {
                      const idx = COLUMNS.findIndex((c) => c.key === col.key);
                      if (idx > 0) advance(it, COLUMNS[idx - 1].key);
                    }}
                    busy={!!busy[it.line.id]}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}


function TicketCard({ item, col, onAdvance, onRevert, busy }) {
  const line = item.line;
  const kind = KIND_META[line.kind] || KIND_META.custom;
  const KindIcon = kind.icon;
  // Compute the reference timestamp for the age badge:
  // - pending column: seconds since pending_at
  // - preparing: seconds since preparing_at (how long they've been making it)
  // - ready: seconds since ready_at (how long it's sitting on the pass)
  // - served: seconds since served_at (just for info)
  const tsField = col.key === "pending" ? "pending_at"
    : col.key === "preparing" ? "preparing_at"
    : col.key === "ready" ? "ready_at"
    : "served_at";
  const ts = line[tsField] || line.pending_at || line.added_at;
  const alert = alertLevel(ts, line.prep_status);
  const alertClasses =
    alert === "critical"
      ? "border-red-500/70 ring-2 ring-red-500/40 shadow-[0_0_20px_-4px_rgba(239,68,68,0.55)] animate-pulse"
      : alert === "warn"
        ? "ring-1 ring-amber-400/40"
        : "";

  return (
    <div
      className={`rounded-xl border p-3 ${kind.color} ${alertClasses} ${busy ? "opacity-60" : ""}`}
      data-testid={`pos-bar-ticket-${line.id}`}
      data-alert={alert || "none"}
    >
      {alert === "critical" && (
        <div className="mb-2 rounded-lg bg-red-500/15 border border-red-500/50 px-2 py-1 flex items-center gap-1.5 text-[11px] text-red-300 font-medium" data-testid={`pos-bar-ticket-${line.id}-alert`}>
          <AlertTriangle className="w-3.5 h-3.5" /> Ticket {elapsed(ts)} — traiter en priorité
        </div>
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] text-muted-foreground">
            <KindIcon className="w-3 h-3" /> {kind.label}
            {item.client_ordered && (
              <span className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-primary bg-primary/10 border border-primary/30 rounded-full px-2 py-0.5">
                🔔 Client
              </span>
            )}
          </div>
          <div className="font-medium text-base truncate mt-0.5">
            {line.quantity > 1 && <span className="text-primary">{line.quantity}× </span>}
            {line.name}
          </div>
          {item.client_name && (
            <div className="text-[11px] text-muted-foreground mt-0.5">
              👤 {item.client_name}
            </div>
          )}
          {line.note && <div className="text-xs text-muted-foreground mt-1 italic">« {line.note} »</div>}
          {/* Recipe — barman sees ingredients + quantities to prepare */}
          {line.kind === "cocktail" && Array.isArray(line.recipe) && line.recipe.length > 0 && (
            <div className="mt-2 rounded-lg bg-black/25 border border-border/40 p-2 space-y-0.5">
              <div className="text-[10px] uppercase tracking-[0.16em] text-primary/80 mb-1">Recette</div>
              {line.recipe.map((ing, idx) => (
                <div key={`${ing.name}-${idx}`} className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground/90">{ing.name}</span>
                  <span className="text-muted-foreground font-mono">
                    {line.quantity > 1 && <span className="text-primary mr-1">×{line.quantity}</span>}
                    {ing.quantity} {ing.unit}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="text-right flex-shrink-0 max-w-[110px]">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Table</div>
          <div className="font-display text-lg leading-tight truncate" title={item.table_label}>{item.table_label}</div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className={`text-xs ${ageBadgeClass(ts, col.key)} flex items-center gap-1`}>
          <Clock className="w-3 h-3" /> {elapsed(ts)}
        </div>

        <div className="flex items-center gap-1">
          {col.key !== "pending" && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onRevert}
              disabled={busy}
              title="Revenir en arrière"
              data-testid={`pos-bar-ticket-${line.id}-revert`}
            >
              <Undo2 className="w-3.5 h-3.5" />
            </Button>
          )}
          {col.next ? (
            <Button
              onClick={onAdvance}
              disabled={busy}
              size="sm"
              className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-3"
              data-testid={`pos-bar-ticket-${line.id}-advance`}
            >
              {col.next === "preparing" && "Commencer"}
              {col.next === "ready" && "Prêt"}
              {col.next === "served" && "Servi"}
              <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground italic">terminé</span>
          )}
        </div>
      </div>
    </div>
  );
}
